import classNames from "classnames";
import type { InputHTMLAttributes } from "react";
import { ErrorWrapper } from "../Error/ErrorWrapper.tsx";
import { Icon, type IconName } from "../Icon/Icon.tsx";

export const TextField = ({
  value,
  onChange,
  label,
  placeholder,
  size,
  className,
  inputClassName,
  iconName,
  suffix,
  onKeyDown,
  onIconDrag,
  errors,
  ...rest
}: {
  value: string;
  onChange: (text: string) => void;
  className?: string;
  inputClassName?: string;
  label?: string;
  placeholder?: string;
  iconName?: IconName;
  suffix?: string;
  size: "sm" | "md" | "lg";
  errors?: string[];
  onIconDrag?: (deltaX: number) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size" | "onChange" | "value" | "onKeyDown"
>) => (
  <ErrorWrapper errors={errors}>
    <div className={classNames("flex-col gap-100", className)}>
      {label && (
        <div
          className={size === "lg" ? "body-lg-semibold" : "body-md-semibold"}
        >
          {label}
        </div>
      )}
      <div
        className={classNames(
          "w-full relative flex-row items-center rounded-150",
          errors && errors.length > 0 && "outline-error",
        )}
      >
        <input
          className={classNames(
            TextInputClassName,
            // XXX: we use padding instead of height to avoid https://github.com/facebook/react-native/issues/28078
            size === "sm"
              ? "px-150 py-[3px] label-md-default"
              : size === "md"
              ? "px-150 py-[7px] label-md-default"
              : "px-200 py-[9px] label-lg-default",
            iconName ? "pl-800" : undefined,
            suffix ? "pr-600" : undefined,
            inputClassName,
          )}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
            onKeyDown?.(event);
          }}
          placeholder={placeholder}
          {...rest}
        />
        {iconName && (
          <div className="absolute left-200 flex-row-center">
            <Icon
              name={iconName}
              size={size === "lg" ? "md" : "sm"}
              className="stroke-secondary-rest"
            />
            {onIconDrag && (
              <span
                onMouseDown={(e) => {
                  e.preventDefault();
                  const startX = e.clientX;

                  const onMouseMove = (moveEvent: MouseEvent) => {
                    const dx = moveEvent.clientX - startX;
                    onIconDrag(dx);
                  };

                  const stop = () => {
                    window.removeEventListener("mousemove", onMouseMove);
                    window.removeEventListener("mouseup", stop);
                    document.body.style.cursor = "";
                    (document.body.style as any).userSelect = "";
                  };

                  window.addEventListener("mousemove", onMouseMove);
                  window.addEventListener("mouseup", stop);
                  document.body.style.cursor = "ew-resize";
                  (document.body.style as any).userSelect = "none";
                }}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 16,
                  cursor: "ew-resize",
                  background: "transparent",
                }}
              />
            )}
          </div>
        )}
        {suffix && (
          <div className="absolute right-200 flex-row-center">
            {suffix && (
              <div
                className={
                  size === "sm"
                    ? "label-md-default"
                    : size === "md"
                    ? "label-md-default"
                    : "label-lg-default"
                }
              >
                {suffix}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  </ErrorWrapper>
);

export const TextInputClassName = classNames(
  "flex-fill rounded-150 placeholder:text-secondary",
  "border border-transparent",
  "hover:enabled:border-transparent focus:enabled:border-input-border-active ",
  "bg-input-surface-rest hover:enabled:bg-input-surface-hover focus:enabled:bg-input-surface-active disabled:bg-input-surface-disabled",
);
